import { getJobTicketFilename, getPreflightReportFilename, getProjectJsonFilename } from './createJobTicket';
import { getPrintGeometry } from './printGeometry';
import type { CalendarProject } from '../types/calendarProject';
import type { OrderDraft, OrderDraftBilling, OrderDraftConsent, OrderDraftCustomer, OrderDraftDelivery, OrderDraftUiStatus, OrderDraftValidationSummary } from '../types/orderDraft';

const productNames = {
  basic_year_story: 'Basic Year Story',
  family_planner_cz: 'Family Planner CZ',
  fineart_gallery: 'FineArt Gallery',
};

const productPlaceholderPrices = {
  basic_year_story: 578,
  family_planner_cz: 690,
  fineart_gallery: 890,
};

type OrderDraftFormData = Partial<{
  customer: Partial<OrderDraftCustomer>;
  delivery: Partial<OrderDraftDelivery>;
  billing: Partial<OrderDraftBilling>;
  consent: Partial<OrderDraftConsent>;
}>;

function createOrderId(projectId: string) {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `order_${crypto.randomUUID()}`;
  }

  return `order_${projectId}_${Date.now().toString(36)}`;
}

function getProjectTitle(project: CalendarProject) {
  return project.title?.trim() || `${productNames[project.productId]} ${project.period.year}`;
}

function getDeliveryPrice(method: OrderDraftDelivery['method']) {
  if (method === 'pickup') return 0;
  return 99;
}

function createPricing(project: CalendarProject, method: OrderDraftDelivery['method']) {
  const productPrice = productPlaceholderPrices[project.productId];
  const deliveryPrice = getDeliveryPrice(method);
  const totalWithVat = productPrice + deliveryPrice;
  return {
    currency: 'CZK' as const,
    productPrice,
    deliveryPrice,
    vatRate: 0.21,
    vatAmount: Math.round(totalWithVat * 21 / 121),
    totalWithVat,
    isPlaceholder: true,
    pricingNote: 'Finální kalkulace bude později napojená na produkční ceník.',
  };
}


export function createOrderDraftValidationSummary(draft: OrderDraft): OrderDraftValidationSummary {
  const missingFields: string[] = [];
  const requiredConsentsMissing: string[] = [];

  if (!draft.customer.fullName.trim()) missingFields.push('Jméno a příjmení');
  if (!draft.customer.email.trim()) missingFields.push('E-mail');
  else if (!draft.customer.email.includes('@') || !draft.customer.email.includes('.')) missingFields.push('Platný e-mail');

  if (draft.delivery.method === 'courier' || draft.delivery.method === 'packet') {
    if (!draft.delivery.street.trim()) missingFields.push('Doručovací ulice');
    if (!draft.delivery.city.trim()) missingFields.push('Doručovací město');
    if (!draft.delivery.postalCode.trim()) missingFields.push('Doručovací PSČ');
    if (!draft.delivery.country.trim()) missingFields.push('Země doručení');
  }

  if (draft.billing.type === 'company') {
    if (!draft.billing.companyName.trim()) missingFields.push('Název firmy');
    if (!draft.billing.ico.trim()) missingFields.push('IČO');
  }

  if (!draft.billing.sameAsDelivery) {
    if (!draft.billing.street.trim()) missingFields.push('Fakturační ulice');
    if (!draft.billing.city.trim()) missingFields.push('Fakturační město');
    if (!draft.billing.postalCode.trim()) missingFields.push('Fakturační PSČ');
    if (!draft.billing.country.trim()) missingFields.push('Země fakturace');
  }

  if (!draft.consent.termsAccepted) requiredConsentsMissing.push('Obchodní podmínky');
  if (!draft.consent.printApprovalAccepted) requiredConsentsMissing.push('Schválení návrhu k výrobě');
  if (!draft.consent.localAssetNoticeAccepted) requiredConsentsMissing.push('Lokální uložení fotek v prohlížeči');

  const productionBlocked = draft.production.hasErrors || Boolean(draft.production.productionBlockedReason);

  return {
    isValid: missingFields.length === 0 && requiredConsentsMissing.length === 0,
    missingFields,
    requiredConsentsMissing,
    productionBlocked,
  };
}

export function getOrderDraftUiStatus(draft: OrderDraft): OrderDraftUiStatus {
  if (draft.production.hasErrors) return 'preflight-errors';
  const validationSummary = createOrderDraftValidationSummary(draft);
  if (!validationSummary.isValid) return 'missing-data';
  return 'draft-ready';
}

export function ensureOrderDraftV02(draft: OrderDraft): OrderDraft {
  const validationSummary = createOrderDraftValidationSummary(draft);
  return {
    ...draft,
    version: '0.2',
    uiStatus: getOrderDraftUiStatus(draft),
    validationSummary,
  };
}

function getPreflightCounts(project: CalendarProject) {
  const report = project.preflight.lastReport;
  const warnings = report?.warningCount ?? project.preflight.issues.filter((issue) => issue.severity === 'warning').length;
  const errors = report?.errorCount ?? project.preflight.issues.filter((issue) => issue.severity === 'error').length;
  return { warnings, errors };
}

export function createOrderDraft(project: CalendarProject): OrderDraft {
  const now = new Date().toISOString();
  const geometry = getPrintGeometry(project.productId);
  const { warnings, errors } = getPreflightCounts(project);
  const assetStorageMode = project.photos.find((photo) => photo.assetStorage)?.assetStorage ?? 'indexeddb-local-mvp';
  const localAssetCount = project.photos.filter((photo) => photo.hasLocalAsset === true).length;
  const cloudAssetCount = project.photos.filter((photo) => photo.cloudAsset?.objectKey || photo.uploadStatus === 'uploaded').length;
  const productionNotes = [
    'Order draft je pouze lokální metadata export bez platby a bez odeslání.',
    'HTML print preview není finální produkční PDF/X.',
  ];

  if (assetStorageMode === 'indexeddb-local-mvp') {
    productionNotes.push('Pro reálnou výrobu bude později nutný cloud upload originálů.');
  }

  return ensureOrderDraftV02({
    id: createOrderId(project.id),
    projectId: project.id,
    createdAt: now,
    updatedAt: now,
    status: 'draft',
    projectSummary: {
      projectId: project.id,
      title: getProjectTitle(project),
      productId: project.productId,
      productName: productNames[project.productId],
      periodYear: project.period.year,
      startMonth: project.period.startMonth,
      monthsCount: project.period.monthsCount,
      themeId: project.themeId,
      pageCount: project.pages.length,
      photoCount: project.photos.length,
    },
    customer: { fullName: '', email: '', phone: '' },
    delivery: { method: 'pickup', street: '', city: '', postalCode: '', country: 'CZ', note: '' },
    billing: { type: 'personal', sameAsDelivery: true, companyName: '', ico: '', dic: '', street: '', city: '', postalCode: '', country: 'CZ' },
    consent: { termsAccepted: false, printApprovalAccepted: false, localAssetNoticeAccepted: false, marketingAccepted: false },
    pricing: createPricing(project, 'pickup'),
    production: {
      productName: productNames[project.productId],
      format: geometry.formatLabel,
      pageCount: project.pages.length,
      preflightStatus: project.preflight.status,
      hasErrors: errors > 0,
      hasWarnings: warnings > 0,
      assetStorageMode,
      localAssetCount,
      cloudAssetCount,
      productionBlockedReason: errors > 0 ? 'Preflight obsahuje chyby.' : undefined,
      productionNotes,
    },
    files: {
      projectJsonFilename: getProjectJsonFilename(project.id),
      preflightReportFilename: getPreflightReportFilename(project.id),
      jobTicketFilename: getJobTicketFilename(project.id),
      productionPdfFilename: 'not-generated-in-mvp',
      assetsPackageFilename: 'not-generated-in-mvp',
    },
  });
}

export function updateOrderDraftFromForm(draft: OrderDraft, formData: OrderDraftFormData): OrderDraft {
  const delivery = { ...draft.delivery, ...formData.delivery };
  return ensureOrderDraftV02({
    ...draft,
    updatedAt: new Date().toISOString(),
    customer: { ...draft.customer, ...formData.customer },
    delivery,
    billing: { ...draft.billing, ...formData.billing },
    consent: { ...draft.consent, ...formData.consent },
    pricing: createPricing({ productId: draft.projectSummary.productId } as CalendarProject, delivery.method),
  });
}

export function getOrderDraftFilename(draft: OrderDraft) {
  return `calendar-studio-order-draft-${draft.id}.json`;
}
